/**
 * T-0800 — the Scope panel: the reverse compile of a comparison's baseline
 * side, in reviewer order, with the completeness gaps beside it.
 *
 * One fetch of
 * `GET /comparisons/{id}/reverse-compile?side=baseline&sort=confidence&order=asc&gaps=true`
 * feeds the whole panel. That call is also the capability probe: the route is
 * mounted only when `REVERSE_COMPILE_ENABLED` is set on the server, so a 404
 * means there is nothing to review here and `ComparisonReportPage` hides the
 * tab. A reader who arrived on `?view=scope` still sees the tab's absence
 * explained, in place, rather than an empty page.
 *
 * Baseline side only. The comparison side is a later toggle (T-1104), which
 * extends this panel rather than replacing it.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, Loader2 } from 'lucide-react';

import { ComparisonsAPI, type ApiRequestError } from '../../lib/comparisonsApi';
import { cn } from '../../lib/utils';
import type {
  CompiledLine,
  IdenticalDuplicate,
  InclusionDrivenDuplicate,
  ReverseCompiled,
  SuggestionItem,
} from '../../types/reverseCompile';

/** The buckets the histogram reports, in the order a reviewer reads them. */
const BUCKETS = ['0.5', '0.6', '0.7', '0.9', 'other'] as const;

const TIER_CLS: Record<string, string> = {
  required:
    'border-rose-200 dark:border-rose-500/30 bg-rose-50 dark:bg-rose-500/15 text-rose-800 dark:text-rose-300',
  recommended:
    'border-amber-200 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-500/15 text-amber-800 dark:text-amber-300',
  consider: 'border bg-muted text-muted-foreground',
};

export type ReverseCompileState =
  | { status: 'loading' }
  /** The route answered 404: not mounted on this server, or no snapshot. */
  | { status: 'unavailable'; message: string }
  | { status: 'error'; message: string }
  | { status: 'ready'; data: ReverseCompiled };

/** True when the reader asked for the Scope view in the URL (`?view=scope`). */
export function urlWantsScope(): boolean {
  if (typeof window === 'undefined') return false;
  return new URLSearchParams(window.location.search).get('view') === 'scope';
}

/**
 * Fetch the reverse compile once for `comparisonId`, and report whether this
 * server offers it at all. Both the tab's visibility and the panel's body read
 * the one result.
 */
export function useReverseCompile(comparisonId: string | undefined): ReverseCompileState {
  const [state, setState] = useState<ReverseCompileState>({ status: 'loading' });

  const load = useCallback(async () => {
    if (!comparisonId) return;
    setState({ status: 'loading' });
    try {
      const data = await ComparisonsAPI.getReverseCompile(comparisonId, {
        side: 'baseline',
        sort: 'confidence',
        order: 'asc',
        gaps: true,
        duplication: true,
      });
      setState({ status: 'ready', data });
    } catch (err) {
      const e = err as ApiRequestError;
      const message = e?.message || 'Reverse compile request failed';
      setState(
        e?.status === 404
          ? { status: 'unavailable', message }
          : { status: 'error', message },
      );
    }
  }, [comparisonId]);

  useEffect(() => {
    void load();
  }, [load]);

  return state;
}

const Chip: React.FC<{
  label: string;
  value: React.ReactNode;
  cls?: string;
  testId?: string;
}> = ({ label, value, cls, testId }) => (
  <div
    data-testid={testId}
    className={cn('rounded-md border px-2 py-1 text-center', cls || 'bg-card')}
  >
    <div className="text-sm font-semibold tabular-nums">{value}</div>
    <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
  </div>
);

const ReviewBadge: React.FC<{ status: string }> = ({ status }) => {
  const needs = status === 'needs_confirmation';
  return (
    <span
      className={cn(
        'whitespace-nowrap rounded px-1.5 py-0.5 text-[10px] font-medium',
        needs
          ? 'bg-amber-100 dark:bg-amber-500/20 text-amber-800 dark:text-amber-300'
          : 'bg-emerald-100 dark:bg-emerald-500/20 text-emerald-800 dark:text-emerald-300',
      )}
    >
      {needs ? 'Needs confirmation' : 'Supported'}
    </span>
  );
};

const LineRow: React.FC<{ line: CompiledLine }> = ({ line }) => (
  <tr className="border-t align-top">
    <td className="px-2 py-1 tabular-nums font-semibold">{line.confidence.toFixed(2)}</td>
    <td className="px-2 py-1">
      <ReviewBadge status={line.review_status} />
    </td>
    <td className="px-2 py-1">{line.room || '—'}</td>
    <td className="whitespace-nowrap px-2 py-1 font-mono text-[11px]">
      {`${line.cat} ${line.sel} ${line.activity}`.trim()}
    </td>
    <td className="px-2 py-1">{line.description}</td>
    <td className="whitespace-nowrap px-2 py-1 tabular-nums">
      {line.qty} {line.unit}
    </td>
    <td className="px-2 py-1">
      {line.code_status === 'unverified' ? (
        <span className="whitespace-nowrap rounded bg-rose-100 dark:bg-rose-500/20 px-1.5 py-0.5 text-[10px] font-semibold text-rose-800 dark:text-rose-300">
          UNVERIFIED
        </span>
      ) : (
        <span className="text-[11px] text-muted-foreground">{line.code_status}</span>
      )}
    </td>
  </tr>
);

const GapRow: React.FC<{ room: string; s: SuggestionItem }> = ({ room, s }) => (
  <li className="flex flex-wrap items-baseline gap-2 border-t px-3 py-1.5 text-xs">
    <span className="font-mono text-[11px] font-semibold">{s.xactimate_code}</span>
    <span className="text-muted-foreground">{room}</span>
    <span className="flex-1">{s.description}</span>
    <span className={cn('rounded border px-1.5 py-0.5 text-[10px]', TIER_CLS[s.tier])}>
      {s.tier}
    </span>
    <span className="text-[10px] uppercase tracking-wide text-muted-foreground">{s.source}</span>
  </li>
);

const Tag: React.FC<{ children: React.ReactNode; cls?: string }> = ({ children, cls }) => (
  <span
    className={cn(
      'whitespace-nowrap rounded border px-1.5 py-0.5 text-[10px]',
      cls || 'bg-muted text-muted-foreground',
    )}
  >
    {children}
  </span>
);

/**
 * One line's operation the inclusion record says another line already covers.
 * The reviewer overturns it from what is on the row: which line, which line
 * covers it, which operation, the assertion id, and how strong the evidence is.
 */
const InclusionDupRow: React.FC<{ d: InclusionDrivenDuplicate }> = ({ d }) => (
  <li className="border-t px-3 py-2 text-xs">
    <div className="flex flex-wrap items-baseline gap-2">
      <span className="text-muted-foreground">{d.room}</span>
      <span className="font-mono text-[11px] font-semibold">
        {`${d.covered.cat} ${d.covered.sel}${d.covered.activity}`.trim()}
      </span>
      <span className="text-muted-foreground">covered by</span>
      <span className="font-mono text-[11px] font-semibold">
        {`${d.covering.cat} ${d.covering.sel}${d.covering.activity}`.trim()}
      </span>
      <span className="flex-1">{d.operation_kind}</span>
      {d.mutual && <Tag>both ways</Tag>}
      {d.weak_evidence && <Tag>glyph evidence</Tag>}
      {d.identity_gap && <Tag>identity: {d.identity_gap}</Tag>}
      <Tag
        cls={
          d.confidence === 'supported'
            ? 'border-emerald-200 dark:border-emerald-500/30 bg-emerald-50 dark:bg-emerald-500/15 text-emerald-800 dark:text-emerald-300'
            : 'border-amber-200 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-500/15 text-amber-800 dark:text-amber-300'
        }
      >
        {d.confidence}
      </Tag>
      <Tag>{d.allowlist_live ? 'live pair' : 'shadow'}</Tag>
    </div>
    <div className="mt-0.5 text-[10px] text-muted-foreground">
      {d.covered.description} · {d.covered.qty} {d.covered.unit} · {d.citation} · assertion{' '}
      <span className="font-mono">{d.assertion_id || '—'}</span>
    </div>
    {d.caveats.map((c) => (
      <div key={c} className="mt-0.5 text-[10px] text-amber-800 dark:text-amber-300">
        · {c}
      </div>
    ))}
  </li>
);

/** Lines sharing room, code and activity — the string backstop's finding. */
const IdenticalDupRow: React.FC<{ d: IdenticalDuplicate }> = ({ d }) => (
  <li className="flex flex-wrap items-baseline gap-2 border-t px-3 py-1.5 text-xs">
    <span className="font-mono text-[11px] font-semibold">
      {`${d.cat} ${d.sel}${d.activity}`.trim()}
    </span>
    <span className="text-muted-foreground">{d.room || '—'}</span>
    <span className="flex-1">{d.lines[0]?.description || d.key}</span>
    <span className="tabular-nums text-[10px] text-muted-foreground">
      {d.lines.map((ln) => `${ln.qty} ${ln.unit}`).join(' + ')}
    </span>
    <Tag>{d.lines.length} lines</Tag>
  </li>
);

const Notice: React.FC<{ children: React.ReactNode; tone?: 'muted' | 'error' }> = ({
  children,
  tone = 'muted',
}) => (
  <div
    className={cn(
      'flex items-center gap-2 rounded-lg border p-4 text-sm',
      tone === 'error'
        ? 'border-red-200 dark:border-red-500/30 bg-red-50 dark:bg-red-500/15 text-red-800 dark:text-red-300'
        : 'bg-card text-muted-foreground',
    )}
  >
    <AlertTriangle className="h-4 w-4 shrink-0" />
    {children}
  </div>
);

const ReverseCompilePanel: React.FC<{ state: ReverseCompileState }> = ({ state }) => {
  if (state.status === 'loading') {
    return (
      <div className="flex items-center justify-center gap-2 rounded-lg border bg-card p-8 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Reverse-compiling the baseline…
      </div>
    );
  }

  if (state.status === 'unavailable') {
    return (
      <Notice>
        Reverse compile is not enabled on this server. ({state.message})
      </Notice>
    );
  }

  if (state.status === 'error') {
    return <Notice tone="error">{state.message}</Notice>;
  }

  const { data } = state;
  const r = data.reconciliation;
  const sort = data.sort;
  const hist = sort?.confidence_histogram || null;
  const gapRooms = (data.gaps?.rooms || []).filter((room) => room.suggestions.length > 0);
  const gapCount = gapRooms.reduce((n, room) => n + room.suggestions.length, 0);
  // Absent when the server does not offer the double-charge report
  // (`MISSING_SCOPE_REPORT_ENABLED` off): the section is then not rendered at
  // all, rather than shown empty as if the estimate were clean.
  const dup = data.duplication;

  return (
    <div className="space-y-4">
      {/* Headline: what was read, and how much of it landed. */}
      <section className="rounded-lg border bg-card p-3 shadow-sm">
        <h3 className="text-sm font-semibold">Reverse-compiled scope — baseline</h3>
        <p className="mt-0.5 text-xs text-muted-foreground">
          <span data-testid="rc-headline">
            rooms {r.mapped_rooms}/{r.source_rooms}; lines {r.mapped_lines}/{r.source_lines}; codes{' '}
            {r.codes_resolved.length}/{r.source_codes} resolved
          </span>{' '}
          · catalog <span className="font-mono">{r.catalog}</span>
          {data.price_list && (
            <>
              {' '}
              · price list <span className="font-mono">{data.price_list}</span>
            </>
          )}
        </p>
        <div className="mt-2 grid grid-cols-3 gap-2 sm:grid-cols-7">
          <Chip
            label="Unmapped"
            testId="rc-unmapped"
            value={sort ? sort.unmapped_lines : r.unmapped_lines.length}
            cls="border bg-muted text-muted-foreground"
          />
          <Chip label="Mean" value={hist?.mean != null ? hist.mean.toFixed(2) : '—'} />
          {BUCKETS.map((b) => (
            <Chip key={b} label={b === 'other' ? 'other' : `conf ${b}`} value={hist?.counts?.[b] ?? 0} />
          ))}
        </div>
        {r.codes_unverified.length > 0 && (
          <p className="mt-2 text-[11px] text-rose-800 dark:text-rose-300">
            {r.codes_unverified.length} code
            {r.codes_unverified.length === 1 ? '' : 's'} did not resolve against the catalog:{' '}
            <span className="font-mono">{r.codes_unverified.join(', ')}</span>
          </p>
        )}
      </section>

      {/* The lines, lowest confidence first — the reviewer's work queue. */}
      <section className="rounded-lg border bg-card shadow-sm">
        <div className="border-b px-3 py-1.5">
          <h3 className="text-sm font-semibold">
            Lines by confidence ({data.lines.length}, lowest first)
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-muted/50 text-left text-[11px] uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-2 py-1 font-medium">Conf.</th>
                <th className="px-2 py-1 font-medium">Review</th>
                <th className="px-2 py-1 font-medium">Room</th>
                <th className="px-2 py-1 font-medium">Code</th>
                <th className="px-2 py-1 font-medium">Description</th>
                <th className="px-2 py-1 font-medium">Qty</th>
                <th className="px-2 py-1 font-medium">Code status</th>
              </tr>
            </thead>
            <tbody data-testid="rc-lines">
              {data.lines.map((line) => (
                <LineRow key={line.line_id} line={line} />
              ))}
            </tbody>
          </table>
        </div>
        {data.lines.length === 0 && (
          <p className="px-3 py-4 text-xs text-muted-foreground">No lines mapped.</p>
        )}
      </section>

      {/* Double charges: what is billed twice, from the record and from the string backstop. */}
      {dup && (
        <section className="rounded-lg border bg-card shadow-sm" data-testid="rc-duplication">
          <div className="border-b px-3 py-1.5">
            <h3 className="text-sm font-semibold">
              Double-charged ({dup.inclusion_driven.length + dup.identical.length})
            </h3>
            <p className="mt-0.5 text-[10px] text-muted-foreground" data-testid="rc-dup-headline">
              {dup.inclusion_driven.length} from the inclusion record ·{' '}
              {dup.identical.length} identical code{dup.identical.length === 1 ? '' : 's'} ·{' '}
              {dup.consulted} consultations over {dup.lines_examined} lines. Nothing is
              suppressed. {dup.citation_note}
            </p>
            {dup.price_list_mismatch && (
              <p className="mt-0.5 text-[10px] text-amber-800 dark:text-amber-300">
                The record is asserted for{' '}
                <span className="font-mono">{dup.asserted_price_list}</span>; this estimate is
                priced from{' '}
                <span className="font-mono">{dup.estimate_price_list || 'an unstated list'}</span>.
                Consulted anyway, and said so.
              </p>
            )}
            {dup.degraded && (
              <p className="mt-0.5 text-[10px] text-red-800 dark:text-red-300">
                Incomplete: {dup.degraded_reason}
              </p>
            )}
          </div>
          {dup.inclusion_driven.length === 0 && dup.identical.length === 0 ? (
            <p className="px-3 py-4 text-xs text-muted-foreground">
              Nothing in this estimate is charged twice by either test.
            </p>
          ) : (
            <>
              {dup.inclusion_driven.length > 0 && (
                <ul data-testid="rc-dup-inclusion">
                  {dup.inclusion_driven.map((d) => (
                    <InclusionDupRow
                      key={`${d.covered.line_id}:${d.covering.line_id}:${d.operation_token}`}
                      d={d}
                    />
                  ))}
                </ul>
              )}
              {dup.identical.length > 0 && (
                <ul data-testid="rc-dup-identical">
                  {dup.identical.map((d) => (
                    <IdenticalDupRow key={d.key} d={d} />
                  ))}
                </ul>
              )}
            </>
          )}
        </section>
      )}

      {/* Completeness gaps over the same scope. */}
      <section className="rounded-lg border bg-card shadow-sm">
        <div className="border-b px-3 py-1.5">
          <h3 className="text-sm font-semibold">Gaps ({gapCount})</h3>
        </div>
        {gapCount === 0 ? (
          <p className="px-3 py-4 text-xs text-muted-foreground">
            No completeness gaps suggested for this scope.
          </p>
        ) : (
          <ul data-testid="rc-gaps">
            {gapRooms.map((room) =>
              room.suggestions.map((s) => (
                <GapRow key={`${room.room_name}:${s.suggestion_id}`} room={room.room_name} s={s} />
              )),
            )}
          </ul>
        )}
      </section>
    </div>
  );
};

export default ReverseCompilePanel;
